import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { pairHeadset, getUsersInHeadquarter, getUnpairedHeadsets, assignLicenseToHeadset } from "../helpers/requests";
import { useAuth } from "../../firebase/auth";

const AddHeadsetModal = ({ setShowAddHeadsetModal, headquarterId, licenseId = null }) => {
  const { t } = useTranslation();
  const { headquarterId: authHeadquarterId, userId, userRole } = useAuth(); // Added userRole
  const [pairingCode, setPairingCode] = useState("");
  const [headsetName, setHeadsetName] = useState(""); // New state for Headset Name
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true); // New state for initial data loading
  const [error, setError] = useState("");

  const [availableAdmins, setAvailableAdmins] = useState<any[]>([]); // List of potential admins
  const [selectedAdmins, setSelectedAdmins] = useState<any[]>([]); // List of selected admins

  // New state for mode selection
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [existingHeadsets, setExistingHeadsets] = useState<any[]>([]);
  const [selectedHeadsetId, setSelectedHeadsetId] = useState("");

  const effectiveHeadquarterId = headquarterId || authHeadquarterId;

  // Fetch available admins and set initial admin
  useEffect(() => {
    const fetchData = async () => {
      setIsFetching(true);
      try {
        // Fetch Admins
        if (effectiveHeadquarterId) {
          const users = await getUsersInHeadquarter(effectiveHeadquarterId);
          // Filter only relevant roles
          const filteredAdmins = users.filter((u: any) => 
            u.role === 'headquarterAdmin' || u.role === 'headsetAdmin' || u.role === 'admin' 
          );
          setAvailableAdmins(filteredAdmins);

          // Pre-select current user
          const currentUserInList = filteredAdmins.find((u: any) => u.id === userId);
          
          if (currentUserInList) {
             setSelectedAdmins([currentUserInList]);
          } else if (userId) {
             const currentFirstName = (localStorage.getItem("firstName") || "").replace(/"/g, "");
             const currentLastName = (localStorage.getItem("lastName") || "").replace(/"/g, "");
             
             if (currentFirstName || currentLastName) {
                 const manualUser = {
                     id: userId,
                     firstName: currentFirstName,
                     lastName: currentLastName,
                     role: userRole
                 };
                 setSelectedAdmins([manualUser]);
             }
          }
        }

        // Fetch Unpaired Headsets
        // If global admin (no headquarterId passed to component or auth), fetch all (or handle appropriately)
        // If HQ admin, fetch for their HQ.
        // getUnpairedHeadsets handles the filter on backend if HQ ID is passed.
        const unpaired = await getUnpairedHeadsets(effectiveHeadquarterId);
        setExistingHeadsets(unpaired);

      } catch (e) {
        console.error("Failed to fetch initial data", e);
      } finally {
        setIsFetching(false);
      }
    };
    fetchData();
  }, [effectiveHeadquarterId, userId, userRole]);

  const handleRemoveAdmin = (id: string) => {
    setSelectedAdmins(selectedAdmins.filter(a => a.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (selectedAdmins.length === 0) {
      setError(t("Please_select_at_least_one_manager"));
      setIsLoading(false);
      return;
    }

    const adminIds = selectedAdmins.map(a => a.id);

    try {
      if (mode === 'new') {
        if (!pairingCode) {
          setError(t("Please_enter_a_pairing_code"));
          setIsLoading(false);
          return;
        }
        if (!headsetName) {
          setError(t("Please_enter_headset_name"));
          setIsLoading(false);
          return;
        }

        const response = await pairHeadset(pairingCode, effectiveHeadquarterId, userId, licenseId, headsetName, adminIds);
        if (response.success) {
          window.dispatchEvent(new CustomEvent('headsetAdded', { detail: response.device }));
          setShowAddHeadsetModal(false);
        } else {
          setError(response.error || t("Failed_to_pair_headset"));
        }
      } else {
        // Existing Mode
        if (!selectedHeadsetId) {
          setError(t("Please_select_a_headset_first")); // Reusing existing key or fallback
          setIsLoading(false);
          return;
        }

        const response = await assignLicenseToHeadset(selectedHeadsetId, licenseId, adminIds);
        if (response.success) {
           const selectedHeadset = existingHeadsets.find(h => h.id === selectedHeadsetId);
           if (selectedHeadset) {
               window.dispatchEvent(new CustomEvent('headsetAdded', { 
                   detail: { 
                       id: selectedHeadset.id,
                       deviceId: selectedHeadset.deviceId,
                       name: selectedHeadset.name,
                       licenseId: licenseId 
                   } 
               }));
           }
           setShowAddHeadsetModal(false);
        } else {
           setError(response.error || t("Failed_to_pair_headset"));
        }
      }
    } catch (err: any) {
      setError(err.message || t("Failed_to_pair_headset"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="confirmation-modal">
      <div className="modal-content">
        {isFetching ? (
          <div style={{ textAlign: "center", padding: "20px" }}>{t("Loading...")}</div>
        ) : (
          <>
            <div style={{ textAlign: "center", fontSize: "1.3em", marginTop: "2vh", marginBottom: "2vh" }}>
              {t("Add_New_Headset")}
            </div>

            {/* Mode Toggle */}
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
              <button 
                type="button"
                onClick={() => setMode('new')}
                style={{ 
                  backgroundColor: mode === 'new' ? "#39A686" : "#E0E0E0",
                  color: mode === 'new' ? "white" : "#404040",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "20px 0 0 20px",
                  cursor: "pointer",
                  fontWeight: "bold"
                }}
              >
                {t("Pair_New_Device")}
              </button>
              <button 
                type="button"
                onClick={() => setMode('existing')}
                style={{ 
                  backgroundColor: mode === 'existing' ? "#39A686" : "#E0E0E0",
                  color: mode === 'existing' ? "white" : "#404040",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "0 20px 20px 0",
                  cursor: "pointer",
                  fontWeight: "bold"
                }}
              >
                {t("Select_Existing_Device")}
              </button>
            </div>

            <form onSubmit={handleSubmit} className="m-4">

              {mode === 'new' ? (
                <>
                  {/* Pairing Code */}
                  <div className="mb-3">
                    <label htmlFor="pairingCode" style={{ marginLeft: "20px", color: "#404040", fontWeight: "bold" }}>
                      {t("Pairing_Code")}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="pairingCode"
                      value={pairingCode}
                      onChange={(e) => setPairingCode(e.target.value)}
                      disabled={isLoading}
                      style={{ backgroundColor: "#CCCCCC", borderRadius: "20px", color: "#404040", height: "5vh", maxHeight: "40px", fontSize: "1em", paddingLeft: "20px" }}
                    />
                  </div>

                  {/* Headset Name */}
                  <div className="mb-3">
                    <label htmlFor="headsetName" style={{ marginLeft: "20px", color: "#404040", fontWeight: "bold" }}>
                      {t("Headset_Name")}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="headsetName"
                      value={headsetName}
                      onChange={(e) => setHeadsetName(e.target.value)}
                      style={{ backgroundColor: "#CCCCCC", borderRadius: "20px", color: "#404040", height: "5vh", maxHeight: "40px", fontSize: "1em", paddingLeft: "20px" }}
                    />
                  </div>
                </>
              ) : (
                /* Select Existing Device */
                <div className="mb-3">
                  <label htmlFor="existingHeadset" style={{ marginLeft: "20px", color: "#404040", fontWeight: "bold" }}>
                    {t("Select_headset")}
                  </label>
                  <select
                    id="existingHeadset"
                    className="form-control"
                    value={selectedHeadsetId}
                    onChange={(e) => setSelectedHeadsetId(e.target.value)}
                    disabled={isLoading}
                    style={{ backgroundColor: "#CCCCCC", borderRadius: "20px", color: "#404040", height: "5vh", maxHeight: "40px", fontSize: "1em", paddingLeft: "20px" }}
                  >
                    <option value="" disabled hidden>{t("Select_headset")}</option>
                    {existingHeadsets.length === 0 ? (
                        <option value="" disabled>{t("No_free_headsets_available")}</option>
                    ) : (
                        existingHeadsets.map(h => (
                            <option key={h.id} value={h.id}>
                                {h.name || h.deviceId}
                            </option>
                        ))
                    )}
                  </select>
                </div>
              )}

              {/* Headset Managers - Common for both modes */}
              <div className="mb-3">
                <label style={{ marginLeft: "20px", color: "#404040", fontWeight: "bold" }}>
                  {t("Headset_Manager")}
                </label>
                
                {/* List of selected admins */}
                <div style={{ marginBottom: "10px", marginLeft: "20px" }}>
                  {selectedAdmins.map(admin => (
                    <div key={admin.id} style={{ display: "flex", alignItems: "center", marginBottom: "5px" }}>
                      <span style={{ marginRight: "10px" }}>
                        {admin.firstName} {admin.lastName} ({t(admin.role)})
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAdmin(admin.id)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#A63939",
                          fontWeight: "bold",
                          cursor: "pointer"
                        }}
                      >
                        X
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add admin dropdown */}
                <div style={{ display: "flex", alignItems: "center" }}>
                  <select
                    className="form-control"
                    value=""
                    onChange={(e) => {
                      const adminId = e.target.value;
                      if (!adminId) return;
                      const adminToAdd = availableAdmins.find(a => a.id === adminId);
                      if (adminToAdd && !selectedAdmins.find(a => a.id === adminToAdd.id)) {
                        setSelectedAdmins([...selectedAdmins, adminToAdd]);
                      }
                    }}
                    style={{ 
                      backgroundColor: "#CCCCCC", 
                      borderRadius: "20px", 
                      color: "#404040", 
                      height: "5vh", 
                      maxHeight: "40px", 
                      fontSize: "1em", 
                      paddingLeft: "20px",
                      flex: 1,
                    }}
                  >
                    <option value="" disabled hidden>{t("Select_new_headset_manager")}</option>
                    {availableAdmins
                      .filter(a => !selectedAdmins.find(sa => sa.id === a.id)) // Filter out already selected
                      .map(admin => (
                        <option key={admin.id} value={admin.id}>
                          {admin.firstName} {admin.lastName} ({t(admin.role)})
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {error && (
                <div
                  style={{
                    textAlign: "center",
                    color: "#A63939",
                    fontSize: "0.8em",
                    marginBottom: "1vh",
                  }}
                >
                  {error}
                </div>
              )}
              <div className="button-container">
                <button
                  type="button"
                  className="btn btn-secondary buttonCancel"
                  onClick={() => setShowAddHeadsetModal(false)}
                  disabled={isLoading}
                >
                  {t("Cancel")}
                </button>
                <button type="submit" className="btn btn-primary buttonAcceptGreen" disabled={isLoading}>
                  {isLoading ? t("Pairing...") : t("Pair_Headset")}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default AddHeadsetModal;